
import { defineComponent } from 'vue';
import { IToaXeResponseBase } from '@/core/interfaces/ApiResponses';
import ToaXeService from '@/core/services/ToaXe.service';
import DanhMucToaXeService from '@/core/services/DanhMucToaXe.service';
import { swalErrNotification } from '@/core/helpers/utils';
import { generateTablePdf } from '@/core/helpers/generate-table-pdf';
import PdfViewerPage from '@/components/shared/pdf-viewer/PdfViewerPage.vue';
import { PDF_GENERATE_TABLE_DEFAULTS } from '@/components/shared/pdf-viewer/pdf-content.config';
import { pickDonViPdfExtra } from '@/components/shared/pdf-viewer/pdf-document-header';

/** Column layout for PDF export (list view, no actions). */
const TOA_XE_LIST_PDF_COLUMNS = [
  { header: 'STT', width: 22, align: 'center' as const },
  { header: 'Tên', width: 75, align: 'left' as const },
  { header: 'Số hiệu', width: 35, align: 'left' as const },
  { header: 'Nhóm', width: 45, align: 'left' as const },
  { header: 'Đơn vị', width: 50, align: 'left' as const },
  { header: 'Ghi chú', width: 46, align: 'left' as const },
];

export default defineComponent({
  name: 'ToaXeListViewer',

  components: { PdfViewerPage },

  data() {
    return {
      toaXeItems: [] as IToaXeResponseBase[],
      tenDanhMuc: '',
      pdfSource: null as string | null,
      isGenerating: false,
      tenDonVi: '',
      tenDonViCha: '',
    };
  },

  computed: {
    pdfFileName(): string {
      const slug = this.tenDanhMuc
        ? this.tenDanhMuc.replace(/\s+/g, '-').toLowerCase()
        : 'danh-sach';
      return `danh-sach-toa-xe-${slug}.pdf`;
    },
  },

  methods: {
    formatSttCell(index: number): string {
      return String(index + 1);
    },

    formatNhomCell(item: IToaXeResponseBase): string {
      return item.danh_muc_toa_xe?.[0]?.ten_danh_muc ?? '-';
    },

    formatDonViCell(item: IToaXeResponseBase): string {
      return item.don_vi?.ten_don_vi ?? '-';
    },

    formatGhiChuCell(item: IToaXeResponseBase): string {
      return item.ghi_chu ? item.ghi_chu : '-';
    },

    async loadDanhMucName(id: number) {
      const queryTen = this.$route.query.ten;
      if (typeof queryTen === 'string' && queryTen) {
        this.tenDanhMuc = queryTen;
        return;
      }

      if (!id) {
        this.tenDanhMuc = 'Tất cả nhóm';
        return;
      }

      try {
        const {
          data: { data },
        } = await DanhMucToaXeService.get(id);
        this.tenDanhMuc = data.ten_danh_muc;
      } catch {
        this.tenDanhMuc = '';
      }
    },

    async loadAndGenerate() {
      const idDmToaXe = Number(this.$route.params.idDmToaXe);

      try {
        this.isGenerating = true;
        this.pdfSource = null;

        await this.loadDanhMucName(idDmToaXe);
        const listResult = await ToaXeService.listAllByDanhMuc(
          idDmToaXe || null,
          idDmToaXe ? 100 : 1000
        );
        this.toaXeItems = listResult.items;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(listResult.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;
        this.pdfSource = await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, 'Có lỗi khi tải danh sách toa xe');
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf(): Promise<string> {
      const rows = this.toaXeItems.map((item, index) => [
        this.formatSttCell(index),
        item.ten_toa_xe ?? '',
        item.so_hieu ?? '',
        this.formatNhomCell(item),
        this.formatDonViCell(item),
        this.formatGhiChuCell(item),
      ]);

      return generateTablePdf({
        ...PDF_GENERATE_TABLE_DEFAULTS,
        title: 'DANH SÁCH TOA XE',
        subtitle: this.tenDanhMuc ? `Nhóm toa xe: ${this.tenDanhMuc}` : undefined,
        columns: TOA_XE_LIST_PDF_COLUMNS,
        rows,
        documentHeader: {
          tenDonVi: this.tenDonVi,
          tenDonViCha: this.tenDonViCha,
          variant: 'masterList',
        },
      });
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
